import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UnansweredGroupDto, UnansweredStatus } from '@repo/shared-types';
import { apiFetch } from '../lib/api';

interface UnansweredPage {
  items: UnansweredGroupDto[];
}

/**
 * `GET /api/bots/:botId/unanswered?status=` (spec D5/M5 task-4; triage-state design
 * 2026-08-21 §A/§C) — one screen-sized payload, no pagination: unlike Leads/Conversations,
 * unanswered-question GROUPS are expected to stay small even as raw message volume grows.
 * `status` is part of the query key (not just the URL) so the Open/Dismissed/Answered tabs
 * each get their own cache entry — switching tabs shows that tab's own loading/empty state
 * instead of the previous tab's stale rows while refetching.
 */
export function useUnanswered(botId: string | undefined, status: UnansweredStatus = 'open') {
  return useQuery({
    queryKey: ['bots', botId, 'unanswered', status],
    queryFn: () => apiFetch<UnansweredPage>(`/api/bots/${botId}/unanswered?status=${status}`),
    enabled: !!botId,
  });
}

/**
 * `POST /api/bots/:botId/unanswered/answer` — on success, invalidates the OVERVIEW query
 * (not this screen's own `unanswered` query) so the rail's `RailNav` amber count pill updates
 * (Task 7 — that pill used to live on `BotHeader`'s tab strip; the rail is its reader now).
 * Deliberately does NOT invalidate/refetch `['bots', botId, 'unanswered']` here: doing so
 * would make the just-answered row disappear immediately (the server marks it `status:
 * 'answered'`, so a fresh fetch of this screen's default `status: open` query would simply
 * omit it — triage-state design 2026-08-21 Task 2, replacing the old client-side
 * `answeredTitles` title-match filter) instead of showing the success line in place, per
 * spec D5/handoff §10 ("on success replace editor with the success line").
 */
export function useAnswerUnanswered(botId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { question: string; answer: string }) =>
      apiFetch<{ sourceId: string }>(`/api/bots/${botId}/unanswered/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] }),
  });
}

/**
 * `PATCH /api/bots/:botId/unanswered/:id { status: 'open' | 'dismissed' }` — Dismiss (from the
 * Open tab) / Reopen (from the Dismissed tab). Unlike `useAnswerUnanswered` above, this DOES
 * invalidate the `unanswered` list (every status's cache entry, via the queryKey prefix — no
 * "keep the row visible in place" concern here: Dismiss/Reopen have no success-line editor
 * state to preserve, the row is simply expected to move to its new tab) plus `overview` (the
 * rail's amber count pill, `totals.unansweredGroups` counts `open` only) and `bots` (the
 * switcher/All-bots health pills read the same aggregate).
 */
export function useSetUnansweredStatus(botId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: Extract<UnansweredStatus, 'open' | 'dismissed'> }) =>
      apiFetch<UnansweredGroupDto>(`/api/bots/${botId}/unanswered/${input.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: input.status }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bots', botId, 'unanswered'] });
      void queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] });
      void queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
  });
}
